import React from 'react';
import { TEhrConfig, TEhrStepsConfig, TEhrExternalInfoField } from 'types/ehrConnection';

import spStep1 from './assets/SimplePractice/step1.png';
import spStep2 from './assets/SimplePractice/step2.png';
import spStep3 from './assets/SimplePractice/step3.png';
import spStep4 from './assets/SimplePractice/step4.png';
import spStep5 from './assets/SimplePractice/step5.png';
import spStep6 from './assets/SimplePractice/step6.png';
import spStep7 from './assets/SimplePractice/step7.png';
import hlStep1 from './assets/Healthie/step1.png';
import hlStep2 from './assets/Healthie/step2.png';
import hlStep3 from './assets/Healthie/step3.png';
import hlStep4 from './assets/Healthie/step4.png';
import hlStep5 from './assets/Healthie/step5.png';
import hlStep7 from './assets/Healthie/step7.png';
import iqStep1 from './assets/IntakeQ/step1.png';
import iqStep2 from './assets/IntakeQ/step2.png';
import iqStep3 from './assets/IntakeQ/step3.png';
import iqStep4 from './assets/IntakeQ/step4.png';
import iqStep5 from './assets/IntakeQ/step5.png';
import iqStep6 from './assets/IntakeQ/step6.png';
import tbStep1 from './assets/Tebra/step1.png';
import tbStep2 from './assets/Tebra/step2.png';
import tbStep3 from './assets/Tebra/step3.png';
import tbStep4 from './assets/Tebra/step4.png';
import tbStep5 from './assets/Tebra/step5.png';
import tbStep6 from './assets/Tebra/step6.png';
import tbStep7 from './assets/Tebra/step7.png';

export const EHR_CONFIGS: TEhrConfig[] = [
    { ehr: 'Klarity', verifyType: 'account_password' },
    { ehr: 'SimplePractice', verifyType: 'agent_auto' },
    { ehr: 'Healthie', verifyType: 'agent_auto' },
    { ehr: 'IntakeQ', verifyType: 'agent_auto' },
    { ehr: 'Tebra', verifyType: 'agent_password' },
    { ehr: 'Practice Fusion', verifyType: 'agent_password' },
    { ehr: 'Athena', verifyType: 'agent_password' },
    { ehr: 'ECW', verifyType: 'agent_password' },
    { ehr: 'TherapyNotes', verifyType: 'agent_password' },
    { ehr: 'JaneApp', verifyType: 'agent_password' },
    { ehr: 'Elation', verifyType: 'agent_password' },
    { ehr: 'CharmHealth', verifyType: 'agent_password' },
    { ehr: 'Dr. Chrono', verifyType: 'agent_password' },
    { ehr: 'Optimantra', verifyType: 'agent_auto' },
];

export const EHR_CONFIG_MAP: Record<string, TEhrConfig> = Object.fromEntries(EHR_CONFIGS.map((c) => [c.ehr, c]));

export const EHR_EXTERNAL_INFO_FIELDS: Record<string, TEhrExternalInfoField[]> = {};

export function getEhrSteps(): Record<string, TEhrStepsConfig> {
    return {
        Klarity: {
            steps: [
                {
                    title: (
                        <>
                            Prepare your <b>Klarity</b> login details
                        </>
                    ),
                    content: 'Make sure you have the email address and password you use to log in to Klarity.',
                },
                {
                    title: (
                        <>
                            Share your <b>Klarity</b> account access with Klarity
                        </>
                    ),
                    content:
                        "Share your Klarity account email address and password with the Klarity team. We'll use this to access your account and sync your patients and appointments.",
                },
                {
                    title: (
                        <>
                            Wait for the <b>sync</b> to complete
                        </>
                    ),
                    content: 'Once we have your login details, our team will begin syncing your Klarity data.',
                },
            ],
            helpLink: { url: 'mailto:payer@helloklarity.com', label: 'Need help? Klarity Help Center' },
        },
        SimplePractice: {
            steps: [
                {
                    title: (
                        <>
                            Go to <b>Settings</b> in your SimplePractice account
                        </>
                    ),
                    content: (
                        <>
                            Log into{' '}
                            <a href="https://signin.simplepractice.com/" target="_blank" rel="noopener noreferrer">
                                SimplePractice
                            </a>{' '}
                            and click the gear icon or navigate to Settings from the sidebar.
                        </>
                    ),
                    img: spStep1,
                },
                {
                    title: (
                        <>
                            Navigate to <b>Team, Add team member</b>
                        </>
                    ),
                    content: (
                        <>
                            In Settings, click &quot;Team&quot; in the left menu, then click &quot;Add team member&quot;
                            button. Note: This feature is available on the <strong>Plus plan</strong>.
                        </>
                    ),
                    img: spStep2,
                },
                {
                    title: (
                        <>
                            Enter the <b>agent email</b> as a new team member
                        </>
                    ),
                    content:
                        'Copy the agent email below and paste it into the "Email" field on the team member invite form.',
                    showEmail: true,
                    img: spStep3,
                },
                {
                    title: 'Select a role for the agent',
                    content: (
                        <ul>
                            <li>
                                For basic functionality: choose <strong>Practice Biller</strong> and{' '}
                                <strong>Practice Scheduler</strong>
                            </li>
                            <li>
                                For full access: assign <strong>Practice Manager</strong>
                            </li>
                        </ul>
                    ),
                    img: spStep4,
                },
                {
                    title: (
                        <>
                            Grant all relevant <b>permissions</b>
                        </>
                    ),
                    content:
                        'Check all permission boxes to allow Klarity to manage billing, scheduling, messaging, and records. The more permissions you grant, the more Klarity can automate.',
                    img: spStep5,
                },
                {
                    title: (
                        <>
                            Click <b>Send Invitation</b>
                        </>
                    ),
                    content:
                        'After filling in the email, selecting a role, and granting permissions, click the "Send Invitation" button to invite Klarity to your practice.',
                    img: spStep6,
                },
                {
                    title: (
                        <>
                            Keep <b>two-factor authentication (2FA)</b> turned OFF
                        </>
                    ),
                    content:
                        "If 2FA is enabled on the agent's team member account, Klarity will not be able to sign in and the connection will break. In Settings → Security, ensure that two-factor authentication is not enabled for this account.",
                    img: spStep7,
                    warning: true,
                },
            ],
            helpLink: { url: 'https://support.simplepractice.com/', label: 'Need help? SimplePractice Help Center' },
        },
        Tebra: {
            steps: [
                {
                    title: (
                        <>
                            Log in to your <b>Tebra</b> account
                        </>
                    ),
                    content: (
                        <>
                            Log into{' '}
                            <a href="https://app.kareo.com/v2/#/sign-in" target="_blank" rel="noopener noreferrer">
                                Tebra
                            </a>
                            , then click your profile icon in the top-right corner and select{' '}
                            <strong>&quot;Practice Settings.&quot;</strong>
                        </>
                    ),
                    img: tbStep1,
                },
                {
                    title: (
                        <>
                            Go to the <b>User Settings</b> page
                        </>
                    ),
                    content: (
                        <>
                            From the left sidebar, click <strong>&quot;User Settings.&quot;</strong>
                        </>
                    ),
                    img: tbStep2,
                },
                {
                    title: (
                        <>
                            Start adding a <b>new user</b>
                        </>
                    ),
                    content: (
                        <>
                            Click <strong>&quot;Add New User&quot;</strong> in the top-right corner to begin creating a
                            new user for the agent.
                        </>
                    ),
                    img: tbStep3,
                },
                {
                    title: (
                        <>
                            Enter the <b>agent details</b>
                        </>
                    ),
                    content: (
                        <>
                            Enter the agent&apos;s details, including the email provided by Klarity. For example:
                            <ul>
                                <li>First name: Agent</li>
                                <li>Last name: Klarity</li>
                                <li>Email: the agent email shown below</li>
                            </ul>
                        </>
                    ),
                    showEmail: true,
                    img: tbStep4,
                },
                {
                    title: (
                        <>
                            Create a <b>password</b> for the agent
                        </>
                    ),
                    content:
                        'Create a password for the agent account. Please save this password and share it with the Klarity team, as it will be needed to log in to Tebra and complete the connection.',
                    img: tbStep5,
                },
                {
                    title: (
                        <>
                            Assign <b>roles and permissions</b>
                        </>
                    ),
                    content: (
                        <>
                            Select the appropriate roles for the agent. We recommend granting full access. You may also
                            select additional roles such as:
                            <ul>
                                <li>
                                    <strong>Biller</strong> (for billing tasks)
                                </li>
                                <li>
                                    <strong>Office Staff</strong> (for scheduling and general operations)
                                </li>
                            </ul>
                        </>
                    ),
                    img: tbStep6,
                },
                {
                    title: (
                        <>
                            Create the <b>user</b>
                        </>
                    ),
                    content: (
                        <>
                            Click <strong>&quot;Create User&quot;</strong> to finish setting up the agent account.
                        </>
                    ),
                    img: tbStep7,
                },
                {
                    title: (
                        <>
                            Notify the <b>Klarity team</b> and provide login details
                        </>
                    ),
                    content: (
                        <>
                            Once setup is complete, please notify the Klarity team and{' '}
                            <strong>share the password</strong> you created for the agent account so we can complete the
                            connection.
                        </>
                    ),
                },
            ],
            helpLink: { url: 'https://helpme.tebra.com/', label: 'Need help? Tebra Help Center' },
        },
        Healthie: {
            steps: [
                {
                    title: (
                        <>
                            Log in to your <b>Healthie</b> account
                        </>
                    ),
                    content: (
                        <>
                            Log into{' '}
                            <a
                                href="https://securestaging.gethealthie.com/account/login"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Healthie
                            </a>{' '}
                            and click <strong>Organization</strong> from the sidebar.
                        </>
                    ),
                    img: hlStep1,
                },
                {
                    title: (
                        <>
                            Go to the <b>Members</b> page
                        </>
                    ),
                    content: (
                        <>
                            Go to <strong>Organization → Members</strong>.
                        </>
                    ),
                    img: hlStep2,
                },
                {
                    title: (
                        <>
                            Start adding a new <b>organization member</b>
                        </>
                    ),
                    content: (
                        <>
                            Click <strong>&quot;Create organization member&quot;</strong> in the top-right corner.
                        </>
                    ),
                    img: hlStep3,
                },
                {
                    title: (
                        <>
                            Enter the <b>agent details</b>
                        </>
                    ),
                    content: (
                        <>
                            Add the agent&apos;s name and email address. For example:
                            <ul>
                                <li>First name: Agent</li>
                                <li>Last name: Klarity</li>
                                <li>Email: the agent email shown below</li>
                            </ul>
                        </>
                    ),
                    showEmail: true,
                    img: hlStep4,
                },
                {
                    title: (
                        <>
                            Set <b>account type</b> and <b>permissions</b>
                        </>
                    ),
                    content: (
                        <>
                            Select Standard as the account type. We recommend granting{' '}
                            <strong>admin-level access</strong> to ensure the agent can perform scheduling, billing, and
                            patient management tasks.
                        </>
                    ),
                    img: hlStep5,
                },
                {
                    title: (
                        <>
                            Add the agent to your <b>organization(s)</b>
                        </>
                    ),
                    content: (
                        <>
                            Select the organization(s) you want the agent to be included in. We recommend selecting{' '}
                            <strong>all relevant organizations</strong> to ensure the agent has full access to your
                            data.
                        </>
                    ),
                },
                {
                    title: (
                        <>
                            Create the <b>organization member</b>
                        </>
                    ),
                    content: (
                        <>
                            After completing all fields, click <strong>&quot;Create organization member&quot;</strong>{' '}
                            to add the agent.
                        </>
                    ),
                    img: hlStep7,
                },
            ],
            helpLink: { url: 'https://help.gethealthie.com/', label: 'Need help? Healthie Help Center' },
        },
        IntakeQ: {
            steps: [
                {
                    title: (
                        <>
                            Log in to your <b>IntakeQ</b> account
                        </>
                    ),
                    content: (
                        <>
                            Log into{' '}
                            <a href="https://intakeq.com/signin" target="_blank" rel="noopener noreferrer">
                                IntakeQ
                            </a>{' '}
                            and click <strong>More → Team</strong> from the top navigation.
                        </>
                    ),
                    img: iqStep1,
                },
                {
                    title: (
                        <>
                            Go to the <b>Practitioners</b> page
                        </>
                    ),
                    content: (
                        <>
                            In the Team page, select the <strong>&quot;Practitioners&quot;</strong> tab.
                        </>
                    ),
                    img: iqStep2,
                },
                {
                    title: (
                        <>
                            Start adding a new <b>practitioner</b>
                        </>
                    ),
                    content: (
                        <>
                            Click <strong>+ Add New Practitioner</strong>.<br />
                            Note: The agent must be added as a practitioner (not an assistant) to ensure it has full
                            access to scheduling, patient data, and billing workflows.
                        </>
                    ),
                    img: iqStep3,
                },
                {
                    title: (
                        <>
                            Enter the <b>agent details</b>
                        </>
                    ),
                    content: (
                        <>
                            Enter the agent&apos;s details, including the email provided by Klarity. For example:
                            <ul>
                                <li>First name: Agent</li>
                                <li>Last name: Klarity</li>
                                <li>Email: the agent email shown below</li>
                            </ul>
                        </>
                    ),
                    showEmail: true,
                    img: iqStep4,
                },
                {
                    title: (
                        <>
                            Select the <b>Administrator</b> role
                        </>
                    ),
                    content: (
                        <>
                            When selecting a role, choose <strong>&quot;Administrator&quot;</strong>. This ensures the
                            agent has full access to scheduling, patient data, and billing workflows.
                        </>
                    ),
                    img: iqStep5,
                },
                {
                    title: (
                        <>
                            Create the <b>practitioner account</b>
                        </>
                    ),
                    content: (
                        <>
                            After entering the details and selecting the role, click{' '}
                            <strong>&quot;Add Practitioner&quot;</strong> to create the agent account. An invitation
                            email will be sent to the agent automatically.
                        </>
                    ),
                    img: iqStep6,
                },
                {
                    title: (
                        <>
                            Notify the <b>Klarity team</b>
                        </>
                    ),
                    content:
                        'Please notify the Klarity team that you have added this account to your IntakeQ so we can complete the connection.',
                },
            ],
        },
        Optimantra: {
            steps: [
                {
                    title: (
                        <>
                            Log in to your <b>Optimantra</b> account
                        </>
                    ),
                    content: (
                        <>
                            Log into{' '}
                            <a href="https://www.optimantra.com/" target="_blank" rel="noopener noreferrer">
                                Optimantra
                            </a>{' '}
                            as an administrator so you can invite a new team member.
                        </>
                    ),
                },
                {
                    title: (
                        <>
                            Open the <b>team / user management</b> page
                        </>
                    ),
                    content:
                        'In the admin settings, navigate to the Users (or Team Members) section and start the flow to add a new user.',
                },
                {
                    title: (
                        <>
                            Invite the agent as a <b>new team member</b>
                        </>
                    ),
                    content:
                        'Copy the agent email below and paste it into the email field when adding the new user. Use a name like "Agent Klarity" so it is easy to identify.',
                    showEmail: true,
                },
                {
                    title: (
                        <>
                            Grant <b>full access</b> permissions
                        </>
                    ),
                    content: (
                        <ul>
                            <li>
                                Assign an <strong>Administrator</strong> or <strong>Provider</strong>-equivalent role so
                                the agent can read patients, appointments, encounters, and billing.
                            </li>
                            <li>
                                Make sure two-factor authentication is <strong>not required</strong> for this account.
                            </li>
                        </ul>
                    ),
                },
                {
                    title: (
                        <>
                            Send the <b>invitation</b>
                        </>
                    ),
                    content:
                        'Submit the invite. The agent will accept the invitation automatically via email and finish setting up the account.',
                },
                {
                    title: (
                        <>
                            Wait for Klarity to <b>verify the connection</b>
                        </>
                    ),
                    content:
                        'Once the invite is sent, Klarity will detect the invitation, accept it, set a password, and verify access. This usually takes a couple of minutes — you will see the connection status update automatically.',
                },
            ],
            helpLink: { url: 'https://www.optimantra.com/support', label: 'Need help? Optimantra Support' },
        },
        Athena: {
            steps: [
                {
                    title: (
                        <>
                            Log in to your <b>athenahealth</b> admin account
                        </>
                    ),
                    content: (
                        <>
                            Log into{' '}
                            <a href="https://athenanet.athenahealth.com/" target="_blank" rel="noopener noreferrer">
                                athenahealth
                            </a>{' '}
                            with an account that has administrator privileges so you can invite a new user.
                        </>
                    ),
                },
                {
                    title: (
                        <>
                            Open <b>User Management</b>
                        </>
                    ),
                    content: (
                        <>
                            Navigate to <strong>Settings → Users</strong> to open the user management page.
                        </>
                    ),
                },
                {
                    title: (
                        <>
                            Invite the agent as a <b>new user</b>
                        </>
                    ),
                    content:
                        'Copy the agent email below and paste it into the email field when adding the new user. Use a name like "Agent Klarity" so it is easy to identify.',
                    showEmail: true,
                },
                {
                    title: (
                        <>
                            Grant <b>full permissions</b>
                        </>
                    ),
                    content: (
                        <ul>
                            <li>
                                Assign an <strong>Administrator</strong> or equivalent role so the agent can read
                                patients, appointments, encounters, and billing.
                            </li>
                            <li>
                                Make sure two-factor authentication is <strong>not required</strong> for this account.
                            </li>
                        </ul>
                    ),
                },
                {
                    title: (
                        <>
                            Send the <b>invitation</b>
                        </>
                    ),
                    content:
                        'Submit the invite. The agent will accept the invitation automatically via email and finish setting up the account.',
                },
                {
                    title: (
                        <>
                            Wait for Klarity to <b>verify the connection</b>
                        </>
                    ),
                    content:
                        'Once the invite is sent, Klarity will detect the invitation, accept it, set a password, and verify access. This usually takes a couple of minutes — you will see the connection status update automatically.',
                },
            ],
            helpLink: { url: 'https://docs.athenahealth.com', label: 'Need help? Athena Support' },
        },
    };
}
